import type { BlockDefinitionMeta } from '@tsagko/label-contracts'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useAdapter, useAdapterMode } from '../adapters/AdapterProvider'
import { useConfirm } from '../ui/ConfirmDialog'
import { useToast } from '../ui/Toaster'
import { CardMenu } from './CardMenu'
import { deriveCopyId } from './copy-id'

/**
 * A block meta with no published version AND no draft is a "shell": there is
 * nothing to open by construction — `/blocks/{id}` probes the draft, then the
 * latest published version, and both 404, so the route can only ever render
 * the not-found pane. A host that keeps the block row alive after its only
 * draft is discarded used to emit exactly this (#191), producing a dead card
 * labelled "Draft" that greets the user with "This block doesn't exist or was
 * deleted."
 *
 * The Odoo host now filters shells out of `listBlocks` itself (#263), so
 * against a compliant host this predicate never drops anything. It stays as a
 * client-side net for the hosts it cannot vouch for — an older bridge, or the
 * demo adapter growing the same state — since listing an unopenable card costs
 * the user a dead end either way.
 */
function isOpenableBlock(meta: BlockDefinitionMeta): boolean {
  return meta.latestVersion > 0 || meta.hasDraft
}

export function BlocksGallery() {
  const { t } = useTranslation()
  const adapter = useAdapter()
  const mode = useAdapterMode()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const toast = useToast()
  const [items, setItems] = useState<BlockDefinitionMeta[]>([])
  // Ids of every meta the host LISTED — the set `deriveCopyId` walks to pick a
  // free "<id>-copy". Taken before the `items` display filter on purpose: id
  // uniqueness is not a display concern, so a row hidden by `isOpenableBlock`
  // must still block its id.
  //
  // This is NOT the set of ids that EXIST. Since #263 the host filters shells
  // out of `listBlocks` server-side, so a shell row keeps occupying its id
  // while never reaching this list, and no client-side set can cover it.
  // Reviving one is therefore the host's contract, not this component's: when
  // a draft lands on a leftover shell row the host refreshes the row's
  // name/category from the incoming body (#191, same PR as the filter), so a
  // takeover surfaces under the new identity, not the shell's stale one. From
  // here we only avoid the ids we are shown — that keeps copies from
  // colliding with *visible* rows; hidden-shell collisions are safe by the
  // host contract above.
  const [allIds, setAllIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!adapter) return
    let cancelled = false
    void (async () => {
      const res = await adapter.resources.listBlocks({ limit: 100 })
      if (cancelled) return
      if (res.status === 'ok') {
        setItems(res.data.items.filter(isOpenableBlock))
        setAllIds(res.data.items.map((i) => i.id))
      } else setError(res.error.message)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [adapter])

  // Clear any pending debounce on unmount so setQuery can't fire afterwards.
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current) }, [])

  async function handleDuplicate(item: BlockDefinitionMeta) {
    if (!adapter) return
    // Load the most current body: prefer draft (reflects unsaved edits),
    // fall back to the latest published version. A draft-only block has
    // latestVersion===0, so the draft path is the only option for it.
    let loadRes = await adapter.resources.loadBlock(item.id, 'draft')
    if (loadRes.status !== 'ok') {
      // Only NOT_FOUND means "no draft exists". Any other error (server,
      // conflict, …) must surface and abort — falling through would silently
      // duplicate from a potentially stale published body.
      if (loadRes.error.code !== 'NOT_FOUND') {
        toast.error(t('toast.loadBlockFailed', { message: loadRes.error.message }))
        return
      }
      // No draft — try the latest published version.
      loadRes = await adapter.resources.loadBlock(item.id)
    }
    if (loadRes.status !== 'ok') {
      toast.error(t('toast.loadBlockFailed', { message: loadRes.error.message }))
      return
    }
    const newId = deriveCopyId(item.id, new Set(allIds))
    // The copy starts as an unpublished draft — correct semantics for
    // "duplicate" since the user should review + publish explicitly.
    const copy = {
      ...loadRes.data.resource,
      id: newId,
      name: `${item.name} (copy)`,
      // Reset version so it starts fresh as a draft (version 0 = unpublished).
      version: 0,
      updatedAt: undefined,
    }
    const saveRes = await adapter.resources.saveBlockDraft(newId, copy)
    if (saveRes.status !== 'ok') {
      toast.error(t('toast.duplicateBlockFailed', { message: saveRes.error.message }))
      return
    }
    navigate(`/blocks/${encodeURIComponent(newId)}`)
  }

  async function handleDelete(item: BlockDefinitionMeta) {
    if (!adapter) return
    const confirmed = await confirm({
      title: t('confirm.deleteBlock.title', { name: item.name }),
      body: t('confirm.deleteBlock.body'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      danger: true,
    })
    if (!confirmed) return
    const res = await adapter.resources.deleteBlock(item.id)
    if (res.status !== 'ok') {
      toast.error(t('toast.deleteBlockFailed', { message: res.error.message }))
      return
    }
    setItems((prev) => prev.filter((i) => i.id !== item.id))
    // deleteBlock drops the row entirely, so its id is free again.
    setAllIds((prev) => prev.filter((id) => id !== item.id))
  }

  function handleSearchChange(value: string) {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setQuery(value)
    }, 150)
  }

  function clearSearch() {
    setSearchInput('')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setQuery('')
  }

  const filteredItems = query.trim()
    ? items.filter((item) => {
        const q = query.toLowerCase()
        const category = item.category ?? ''
        // Match the category BOTH ways (#154): the card badge shows the
        // TRANSLATED label, so a Spanish user who reads "· cumplimiento" and
        // types it must get the card back — search has to find what it shows.
        // The stored value stays searchable too, so the contract terms an
        // English-speaking author knows ("compliance") keep working in every
        // locale, and an unknown category (free `z.string()`, translated to
        // itself) is matched by a single arm rather than none.
        const categoryLabel = category
          ? String(t(`category.${category}`, { defaultValue: category }))
          : ''
        return (
          item.name.toLowerCase().includes(q) ||
          item.id.toLowerCase().includes(q) ||
          category.toLowerCase().includes(q) ||
          categoryLabel.toLowerCase().includes(q)
        )
      })
    : items

  if (!adapter || loading) {
    return <div className="p-8 text-sm text-stone-500">{t('gallery.blocks.loading')}</div>
  }

  return (
    <div className="h-full overflow-auto p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">{t('gallery.blocks.title')}</h2>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {t(
              mode === 'embedded' ? 'gallery.blocks.subtitleEmbedded' : 'gallery.blocks.subtitle',
              { count: items.length }
            )}
          </p>
        </div>
        <Link
          to="/blocks/new"
          data-testid="gallery-new-button"
          className="rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
        >
          {t('gallery.blocks.newBlock')}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded border border-rose-300 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">
          {error}
        </div>
      )}

      {items.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <input
            data-testid="gallery-search"
            type="search"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('gallery.blocks.search')}
            className="flex-1 rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-stone-500 dark:border-stone-700 dark:bg-stone-900"
          />
          <span data-testid="gallery-count" className="text-sm text-stone-500 dark:text-stone-400 whitespace-nowrap">
            {query.trim()
              ? t('gallery.common.countFiltered', {
                  shown: filteredItems.length,
                  total: items.length,
                })
              : t('gallery.blocks.count', { count: items.length })}
          </span>
        </div>
      )}

      {items.length === 0 ? (
        <div
          data-testid="gallery-zero-state"
          className="rounded border border-dashed border-stone-300 p-12 text-center dark:border-stone-700"
        >
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t('gallery.blocks.emptyState')}
          </p>
          <Link
            to="/blocks/new"
            data-testid="gallery-zero-state-new"
            className="mt-4 inline-block rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
          >
            {t('gallery.blocks.newBlock')}
          </Link>
        </div>
      ) : filteredItems.length === 0 ? (
        <div
          data-testid="gallery-no-matches"
          className="rounded border border-dashed border-stone-300 p-12 text-center dark:border-stone-700"
        >
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t('gallery.common.noMatches', { query })}
          </p>
          <button
            data-testid="gallery-clear-search"
            type="button"
            onClick={clearSearch}
            className="mt-4 rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
          >
            {t('common.clear')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <Link
            to="/blocks/new"
            className="flex aspect-[4/3] items-center justify-center rounded border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-stone-500 dark:border-stone-700 dark:text-stone-400"
          >
            {t('gallery.templates.startFromBlank')}
          </Link>
          {filteredItems.map(item => (
            <Link
              key={item.id}
              to={`/blocks/${encodeURIComponent(item.id)}`}
              data-testid="gallery-item"
              data-name={item.name}
              className="relative flex aspect-[4/3] flex-col justify-between rounded border border-stone-200 bg-white p-4 text-left shadow-sm transition hover:border-stone-400 dark:border-stone-800 dark:bg-stone-900"
            >
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <h3 className="text-sm font-semibold">{item.name}</h3>
                    <span
                      className={[
                        'rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wide',
                        item.tier === 'platform'
                          ? 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300'
                          : 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300',
                      ].join(' ')}
                    >
                      {t(`tier.${item.tier}`)}
                    </span>
                  </div>
                  <CardMenu
                    items={[
                      {
                        id: 'duplicate',
                        label: t('common.duplicate'),
                        onSelect: () => void handleDuplicate(item),
                      },
                      {
                        id: 'delete',
                        label: t('common.delete'),
                        onSelect: () => void handleDelete(item),
                        danger: true,
                      },
                    ]}
                  />
                </div>
                <div className="mt-1 flex items-center gap-1 text-[11px] text-stone-500 dark:text-stone-400">
                  <span>
                    {item.latestVersion > 0
                      ? `v${item.latestVersion}`
                      : t('gallery.blocks.draftVersion')}
                  </span>
                  {item.hasDraft && item.latestVersion > 0 && (
                    <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-amber-900 dark:bg-amber-700 dark:text-amber-100">
                      {t('gallery.blocks.draftBadge')}
                    </span>
                  )}
                  {/* `defaultValue`: BlockDefinition.category is a free
                      `z.string()` in the contract even though the editor only
                      offers CATEGORIES, so an unknown value renders itself
                      rather than a raw `category.*` key. Search (above)
                      matches this display text AND the stored value. */}
                  {item.category && (
                    <span>· {t(`category.${item.category}`, { defaultValue: item.category })}</span>
                  )}
                </div>
                {item.description && (
                  <p className="mt-2 text-xs text-stone-600 dark:text-stone-400 line-clamp-2">
                    {item.description}
                  </p>
                )}
              </div>
              <p className="text-[10px] text-stone-400">
                {item.updatedAt?.slice(0, 10) ?? '—'}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
